import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Navbar } from "../components/Navbar";
import { JobCard } from "../components/JobCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, MapPin, Filter, X, Briefcase, Building2, User, Layers } from "lucide-react";
import { SETTORI_AZIENDALI } from "../data/settoriSkills";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Città del Canton Ticino
const TICINO_LOCATIONS = [
  { value: "all", label: "Tutte le località" },
  { value: "Residente in Italia (Frontaliere)", label: "Frontaliere (Italia)" },
  { value: "Lugano", label: "Lugano" },
  { value: "Bellinzona", label: "Bellinzona" },
  { value: "Locarno", label: "Locarno" },
  { value: "Mendrisio", label: "Mendrisio" },
  { value: "Chiasso", label: "Chiasso" },
  { value: "Ascona", label: "Ascona" },
  { value: "Biasca", label: "Biasca" },
  { value: "Giubiasco", label: "Giubiasco" },
  { value: "Manno", label: "Manno" },
  { value: "Agno", label: "Agno" }
];

// Settori lavorativi
const SECTORS = [
  { value: "all", label: "Tutti i settori" },
  ...SETTORI_AZIENDALI.map(s => ({ value: s, label: s }))
];

// Tipi di contratto aggiornati
const CONTRACT_TYPES = [
  { value: "all", label: "Tutti i contratti" },
  { value: "tempo_indeterminato", label: "Tempo indeterminato" },
  { value: "tempo_determinato", label: "Tempo determinato" },
  { value: "apprendistato", label: "Apprendistato" },
  { value: "stage", label: "Stage/Tirocinio" },
  { value: "contratto_progetto", label: "Contratto a progetto" },
  { value: "lavoro_temporaneo", label: "Lavoro temporaneo" },
  { value: "freelance", label: "Freelance" },
  { value: "part_time", label: "Part-time" },
  { value: "full_time", label: "Full-time" },
  { value: "stagionale", label: "Stagionale" },
  { value: "a_chiamata", label: "A chiamata" }
];

// Tipi di inserzionista
const ADVERTISER_TYPES = [
  { value: "all", label: "Tutti gli inserzionisti" },
  { value: "company", label: "Aziende" },
  { value: "agency", label: "Agenzie di lavoro" },
  { value: "private", label: "Privati" }
];

export default function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    location: searchParams.get("location") || "all",
    sector: searchParams.get("sector") || "all",
    contract_type: searchParams.get("contract_type") || "all",
    advertiser_type: searchParams.get("advertiser_type") || "all"
  });

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get("search")) params.append("search", searchParams.get("search"));
      if (searchParams.get("location") && searchParams.get("location") !== "all") params.append("location", searchParams.get("location"));
      if (searchParams.get("sector") && searchParams.get("sector") !== "all") params.append("sector", searchParams.get("sector"));
      if (searchParams.get("contract_type") && searchParams.get("contract_type") !== "all") params.append("contract_type", searchParams.get("contract_type"));
      if (searchParams.get("advertiser_type") && searchParams.get("advertiser_type") !== "all") params.append("advertiser_type", searchParams.get("advertiser_type"));
      
      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(response.data);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();
    if (filters.search) newParams.set("search", filters.search);
    if (filters.location && filters.location !== "all") newParams.set("location", filters.location);
    if (filters.sector && filters.sector !== "all") newParams.set("sector", filters.sector);
    if (filters.contract_type && filters.contract_type !== "all") newParams.set("contract_type", filters.contract_type);
    if (filters.advertiser_type && filters.advertiser_type !== "all") newParams.set("advertiser_type", filters.advertiser_type);
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({ search: "", location: "all", sector: "all", contract_type: "all", advertiser_type: "all" });
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = filters.search || 
    (filters.location && filters.location !== "all") || 
    (filters.sector && filters.sector !== "all") ||
    (filters.contract_type && filters.contract_type !== "all") ||
    (filters.advertiser_type && filters.advertiser_type !== "all");

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="jobs-page">
      <Navbar />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Annunci di lavoro
          </h1>
          <p className="text-slate-600">
            Trova la tua prossima opportunità in Canton Ticino
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <form onSubmit={handleSearch} data-testid="jobs-filter-form">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cerca per titolo, azienda..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="jobs-search-input"
                />
              </div>

              {/* Desktop Filters */}
              <div className="hidden lg:flex gap-3">
                <Select
                  value={filters.location}
                  onValueChange={(value) => setFilters({ ...filters, location: value })}
                >
                  <SelectTrigger className="w-[180px]" data-testid="jobs-location-select">
                    <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Località" />
                  </SelectTrigger>
                  <SelectContent>
                    {TICINO_LOCATIONS.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.sector}
                  onValueChange={(value) => setFilters({ ...filters, sector: value })}
                >
                  <SelectTrigger className="w-[180px]" data-testid="jobs-sector-select">
                    <Layers className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Settore" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {SECTORS.map((sector) => (
                      <SelectItem key={sector.value} value={sector.value}>
                        {sector.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.contract_type}
                  onValueChange={(value) => setFilters({ ...filters, contract_type: value })}
                >
                  <SelectTrigger className="w-[180px]" data-testid="jobs-contract-select">
                    <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Tipo contratto" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.advertiser_type}
                  onValueChange={(value) => setFilters({ ...filters, advertiser_type: value })}
                >
                  <SelectTrigger className="w-[180px]" data-testid="jobs-advertiser-select">
                    <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Inserzionista" />
                  </SelectTrigger>
                  <SelectContent>
                    {ADVERTISER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mobile Filter Toggle */}
              <Button
                type="button"
                variant="outline"
                className="lg:hidden"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="mobile-filter-toggle"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtri
              </Button>

              <Button
                type="submit"
                className="bg-[#DA291C] hover:bg-[#B91C1C] text-white"
                data-testid="jobs-search-button"
              >
                Cerca
              </Button>
            </div>

            {/* Mobile Filters */}
            {showFilters && (
              <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200 animate-fadeIn">
                <Select
                  value={filters.location}
                  onValueChange={(value) => setFilters({ ...filters, location: value })}
                >
                  <SelectTrigger data-testid="mobile-location-select">
                    <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Località" />
                  </SelectTrigger>
                  <SelectContent>
                    {TICINO_LOCATIONS.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.sector}
                  onValueChange={(value) => setFilters({ ...filters, sector: value })}
                >
                  <SelectTrigger data-testid="mobile-sector-select">
                    <Layers className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Settore" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {SECTORS.map((sector) => (
                      <SelectItem key={sector.value} value={sector.value}>
                        {sector.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.contract_type}
                  onValueChange={(value) => setFilters({ ...filters, contract_type: value })}
                >
                  <SelectTrigger data-testid="mobile-contract-select">
                    <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Tipo contratto" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.advertiser_type}
                  onValueChange={(value) => setFilters({ ...filters, advertiser_type: value })}
                >
                  <SelectTrigger data-testid="mobile-advertiser-select">
                    <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Inserzionista" />
                  </SelectTrigger>
                  <SelectContent>
                    {ADVERTISER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
              <span className="text-sm text-slate-500">Filtri attivi:</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-[#DA291C] hover:text-[#B91C1C] hover:bg-red-50"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Rimuovi tutti
              </Button>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-slate-600">
            {loading ? "Caricamento..." : `${jobs.length} annunci trovati`}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : jobs.length > 0 ? (
          <div className="space-y-4">
            {jobs.map((job, index) => (
              <div key={job.id} className={`animate-fadeIn stagger-${Math.min(index + 1, 4)}`}>
                <JobCard job={job} />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state bg-white rounded-lg border border-slate-200 py-16">
            <Briefcase className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              Nessun annuncio trovato
            </h3>
            <p className="text-slate-500 mb-4">
              Prova a modificare i filtri di ricerca
            </p>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                data-testid="empty-clear-filters"
              >
                Rimuovi filtri
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
